import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
    getOrderByLabel,
    getOrderItems,
    scanPackItem,
    completePackVerification,
    resetPackVerification,
    clearPackScanResult,
    clearPackVerification
} from '../../store/actions/packVerification.action';
import LoaderSpinner from '../common/Loader';
import DataTable from '../common/DataTable';
import { PackVerificationItemColumns } from '../../constant';
import { toast } from "react-toastify";
import './PickScanDialog.css';

/*istanbul ignore next */
const PackVerificationDialog = (props) => {
    const { modalClass, setModalShow, modalShow, fetchAllList, initialLabel } = props;
    const dispatch = useDispatch();
    const labelInputRef = useRef(null);
    const itemInputRef = useRef(null);

    const [showLoader, setShowLoader] = useState(false);
    const [step, setStep] = useState(initialLabel ? 'scanItems' : 'scanLabel');
    const [labelInput, setLabelInput] = useState('');
    const [itemInput, setItemInput] = useState('');
    const [scanFeedback, setScanFeedback] = useState(null);
    const [orderItemsData, setOrderItemsData] = useState([]);
    const labelTimerRef = useRef(null);
    const itemTimerRef = useRef(null);

    const { packOrder, packOrderStatus, orderItems, orderItemsStatus, lastPackScanResult } = useSelector(
        (state) => state.packVerificationReducer
    );

    // On mount: either auto-load the scanned label, or focus the label input
    useEffect(() => {
        if (initialLabel) {
            dispatch(getOrderByLabel({ orderNumber: initialLabel, companySellerId: 0 }));
        } else {
            setTimeout(() => labelInputRef.current?.focus(), 200);
        }
        return () => {
            dispatch(clearPackVerification());
        };
    }, []);

    // When order loaded, move to scanItems step
    useEffect(() => {
        if (packOrderStatus === 'SUCCESS' && packOrder?.outboundId) {
            setStep('scanItems');
            fetchOrderItems();
            setTimeout(() => itemInputRef.current?.focus(), 200);
        } else if (packOrderStatus === 'FAILED') {
            setScanFeedback({ type: 'error', message: 'Order not found. Check the label and try again.' });
            setTimeout(() => {
                setScanFeedback(null);
                setTimeout(() => labelInputRef.current?.focus(), 100);
            }, 3000);
        }
    }, [packOrder, packOrderStatus]);

    // Handle order items loading
    useEffect(() => {
        if (orderItemsStatus === 'PENDING') {
            setShowLoader(true);
        } else if (orderItemsStatus === 'SUCCESS') {
            setShowLoader(false);
            setOrderItemsData(orderItems || []);
        } else if (orderItemsStatus === 'FAILED') {
            setShowLoader(false);
        }
    }, [orderItemsStatus, orderItems]);

    // Keep the active scan input always focused so the barcode scanner works anywhere.
    useEffect(() => {
        const isAllVerified = () => {
            const items = orderItemsData;
            if (!items.length) return false;
            return items.every(i => i.isVerified);
        };
        const getActiveInput = () => {
            if (step === 'scanLabel') return labelInputRef.current;
            if (step === 'scanItems' && !isAllVerified()) return itemInputRef.current;
            return null;
        };
        const keepFocus = () => {
            const el = getActiveInput();
            if (el && document.activeElement !== el) el.focus();
        };
        keepFocus();
        const interval = setInterval(keepFocus, 500);
        document.addEventListener('click', keepFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('click', keepFocus);
        };
    }, [step, orderItemsData]);

    // Handle pack scan result
    useEffect(() => {
        if (lastPackScanResult) {
            if (lastPackScanResult.isValid) {
                setScanFeedback({
                    type: 'success',
                    message: lastPackScanResult.isOrderComplete
                        ? `ALL ITEMS VERIFIED! Ready to seal and ship.`
                        : `${lastPackScanResult.scannedSKU} - Scanned! (${lastPackScanResult.scannedQuantity}/${lastPackScanResult.expectedQuantity})`
                });
                playSound('success');
                fetchOrderItems();
            } else {
                setScanFeedback({
                    type: 'error',
                    message: lastPackScanResult.message || 'Invalid scan'
                });
                playSound('error');
            }
            setTimeout(() => {
                setScanFeedback(null);
                dispatch(clearPackScanResult());
            }, 3000);
        }
    }, [lastPackScanResult]);

    const playSound = (type) => {
        try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            gainNode.gain.value = 0.3;
            if (type === 'success') {
                oscillator.frequency.value = 880;
                oscillator.type = 'sine';
            } else {
                oscillator.frequency.value = 300;
                oscillator.type = 'square';
            }
            oscillator.start();
            oscillator.stop(audioCtx.currentTime + 0.15);
        } catch (e) {
            // Audio not supported
        }
    };

    const fetchOrderItems = () => {
        if (packOrder?.outboundId) {
            dispatch(getOrderItems({
                outboundId: packOrder.outboundId,
                orderNumber: packOrder.orderNumber
            }));
        }
    };

    const processLabelScan = (rawValue) => {
        const label = (rawValue || '').split(',')[0].trim();
        if (!label) return;
        dispatch(getOrderByLabel({ orderNumber: label, companySellerId: 0 }));
        setLabelInput('');
    };

    const processItemScan = (rawValue) => {
        const sku = (rawValue || '').trim();
        if (!sku) return;
        if (!packOrder?.outboundId) {
            toast.error('No order loaded. Scan a shipping label first.');
            return;
        }
        dispatch(scanPackItem({
            outboundId: packOrder.outboundId,
            orderNumber: packOrder.orderNumber,
            scannedSKU: sku
        }));
        setItemInput('');
        setTimeout(() => itemInputRef.current?.focus(), 100);
    };

    const handleComplete = async () => {
        if (!packOrder?.outboundId) return;
        try {
            setShowLoader(true);
            const res = await Promise.resolve(dispatch(completePackVerification({
                outboundId: packOrder.outboundId,
                orderNumber: packOrder.orderNumber
            })));
            if (res?.data?.Status === 200) {
                toast.success('Pack verification completed!');
                if (fetchAllList) fetchAllList();
                handleClose();
            }
        } catch (err) {
            toast.error('Failed to complete verification');
        }
        setShowLoader(false);
    };

    const handleReset = async () => {
        if (!packOrder?.outboundId) return;
        try {
            setShowLoader(true);
            const res = await Promise.resolve(dispatch(resetPackVerification({
                outboundId: packOrder.outboundId,
                orderNumber: packOrder.orderNumber
            })));
            if (res?.data?.Status === 200) {
                toast.success('Verification reset');
                fetchOrderItems();
            }
        } catch (err) {
            toast.error('Failed to reset verification');
        }
        setShowLoader(false);
    };

    const handleScanNewOrder = () => {
        // Close the modal — user will scan the next label from the main page's scan bar
        handleClose();
    };

    const handleClose = () => {
        dispatch(clearPackVerification());
        setModalShow({ show: false, item: {} });
    };

    const getProgress = () => {
        if (!orderItemsData.length) return { verified: 0, total: 0, percent: 0 };
        const verified = orderItemsData.filter(item => item.isVerified).length;
        const total = orderItemsData.length;
        const percent = total > 0 ? Math.round((verified / total) * 100) : 0;
        return { verified, total, percent };
    };

    const progress = getProgress();
    const allVerified = progress.percent === 100 && orderItemsData.length > 0;

    return (
        <Modal
            className={modalClass || ''}
            size="xl"
            dialogClassName="pack-verify-modal"
            show={modalShow}
            onHide={handleClose}
        >
            <Modal.Header closeButton>
                <div className="modal-main-div viewOrder">
                    <span className="modal-m-text">Pack Verification </span>
                    {packOrder?.orderNumber && (
                        <span className="modal-m-text mr-2">- Order: {packOrder.orderNumber}</span>
                    )}
                    {showLoader && <LoaderSpinner />}
                </div>
                <svg
                    className="btn-cclose close"
                    onClick={handleClose}
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    cursor="pointer"
                >
                    <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
            </Modal.Header>
            <Modal.Body className={`${showLoader && 'disabled'}`}>

                {/* Step 1: Scan Shipping Label */}
                {step === 'scanLabel' && (
                    <div className="text-center py-4">
                        <i className="fa fa-truck" style={{ fontSize: '3em', color: '#6c757d' }}></i>
                        <h5 className="mt-3 mb-4">Scan Shipping Label to Start</h5>
                        <div className="d-flex align-items-center justify-content-center">
                            <input
                                ref={labelInputRef}
                                type="text"
                                className="form-control pick-scan-input"
                                value={labelInput}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setLabelInput(val);
                                    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
                                    if (val.trim()) {
                                        labelTimerRef.current = setTimeout(() => processLabelScan(val), 150);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
                                        processLabelScan(labelInput);
                                    }
                                }}
                                placeholder="Scan shipping label barcode..."
                                autoFocus
                                autoComplete="off"
                            />
                        </div>
                    </div>
                )}

                {/* Step 2: Scan Items */}
                {step === 'scanItems' && packOrder && (
                    <>
                        {/* Order Info */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>Order:</strong> {packOrder.orderNumber}
                                {packOrder.customerName && (
                                    <span className="ml-3"><strong>Customer:</strong> {packOrder.customerName}</span>
                                )}
                            </div>
                            <div>
                                <button className="btn btn-sm btn-outline-secondary mr-2" onClick={handleScanNewOrder}>
                                    <i className="fa fa-exchange" aria-hidden="true"></i> New Order
                                </button>
                                <button className="btn btn-sm btn-outline-danger" onClick={handleReset}>
                                    <i className="fa fa-undo" aria-hidden="true"></i> Reset
                                </button>
                            </div>
                        </div>

                        {/* Progress */}
                        <div className="pick-progress-section mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fontFix">
                                    Verified: {progress.verified} / {progress.total} items ({progress.percent}%)
                                </span>
                                {allVerified && (
                                    <span className="text-success font-weight-bold">
                                        <i className="fa fa-check-circle"></i> ALL VERIFIED
                                    </span>
                                )}
                            </div>
                            <progress
                                className={`pack-progress-native ${allVerified ? 'bg-success' : 'bg-primary'}`}
                                value={progress.percent}
                                max={100}
                                style={{ height: '8px', width: '100%' }}
                            />

                        </div>

                        {/* Scan Feedback */}
                        {scanFeedback && (
                            <div className={`alert pick-scan-feedback pick-scan-${scanFeedback.type} mb-3`}>
                                <span className="pick-scan-icon">
                                    {scanFeedback.type === 'success' && <i className="fa fa-check-circle"></i>}
                                    {scanFeedback.type === 'error' && <i className="fa fa-times-circle"></i>}
                                </span>
                                <span className="pick-scan-message">{scanFeedback.message}</span>
                            </div>
                        )}

                        {/* Scan Item Input */}
                        {!allVerified ? (
                            <div className="mb-3">
                                <div className="d-flex align-items-center">
                                    <label className="fontFix mr-2 mb-0" htmlFor="pack-verify-item-input">Scan Item:</label>
                                    <input
                                        id="pack-verify-item-input"
                                        ref={itemInputRef}
                                        type="text"
                                        className="form-control pick-scan-input"
                                        value={itemInput}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setItemInput(val);
                                            if (itemTimerRef.current) clearTimeout(itemTimerRef.current);
                                            if (val.trim()) {
                                                itemTimerRef.current = setTimeout(() => processItemScan(val), 150);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (itemTimerRef.current) clearTimeout(itemTimerRef.current);
                                                processItemScan(itemInput);
                                            }
                                        }}
                                        placeholder="Scan item barcode..."
                                        autoFocus
                                        autoComplete="off"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center mb-3">
                                <button className="btn btn-success btn-lg" onClick={handleComplete}>
                                    <i className="fa fa-check" aria-hidden="true"></i> Complete Verification & Seal
                                </button>
                            </div>
                        )}

                        {/* Items Table */}
                        <div className="main_content_table_history">
                            <div className="tableFixHeadListing pl-2 zIndex1">
                                <DataTable
                                    headers={PackVerificationItemColumns}
                                    tableData={orderItemsData.map((item, index) => {
                                        let scannedClass = '';
                                        if (item.scannedQuantity >= item.expectedQuantity) {
                                            scannedClass = 'text-success font-weight-bold';
                                        } else if (item.scannedQuantity > 0) {
                                            scannedClass = 'text-primary font-weight-bold';
                                        }
                                        return {
                                            ...item,
                                            listIndex: index,
                                            statusHtml: item.isVerified
                                                ? '<i class="fa fa-check-circle text-success" style="font-size:1.2em"></i>'
                                                : '<i class="fa fa-square-o text-muted" style="font-size:1.2em"></i>',
                                            scannedHtml: '<span class="' + scannedClass + '">' + item.scannedQuantity + '/' + item.expectedQuantity + '</span>',
                                        };
                                    })}
                                    tableDataStatus={orderItemsStatus === 'PENDING' ? 'PENDING' : 'SUCCESS'}
                                    handleSortingColumn={() => {}}
                                    handleCheckRecord={() => {}}
                                />
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

PackVerificationDialog.propTypes = {
    modalClass: PropTypes.string,
    setModalShow: PropTypes.func.isRequired,
    modalShow: PropTypes.bool.isRequired,
    fetchAllList: PropTypes.func,
    initialLabel: PropTypes.string
};

export default PackVerificationDialog;
